/**
 * AdminPurchaseOrdersEnhanced - Enhanced PO list view with standardized table
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useAdminTable } from '../../hooks/useAdminTable';
import AdminDataTable, {
  MonoCell,
  CurrencyCell,
  DateCell,
  StatusCell,
  BulkActionToolbar,
} from '../../components/admin/AdminDataTable';
import AdminListHeader from '../../components/admin/AdminListHeader';
import {
  FilterBar,
  GlobalSearch,
  StatusFilter,
  DateRangeFilter,
  NumericRangeFilter,
  ActiveFilterChips,
} from '../../components/admin/AdminTableFilters';
import {
  DeleteConfirmationModal,
  BulkStatusUpdateModal,
} from '../../components/admin/BulkActionModals';
import { Button } from '../../components/ui/button';
import { purchaseOrdersAPI, vendorsAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Eye,
  FileText,
  Trash2,
  RefreshCw,
  ShoppingBag,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Plus,
  Download,
  FileArchive,
} from 'lucide-react';

// Status configuration
const PO_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  { value: 'sent', label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: Send },
  { value: 'partial', label: 'Partial', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  { value: 'closed', label: 'Closed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
];

const STATUS_CONFIG = PO_STATUSES.reduce((acc, s) => {
  acc[s.value] = s;
  return acc;
}, {});

const AdminPurchaseOrdersEnhanced = ({ onViewPO, onCreatePO }) => {
  const { isAdmin } = useAuth();
  
  // Vendors for filter
  const [vendors, setVendors] = useState([]);
  
  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  
  // Download helper
  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  // Handle single PO PDF download
  const handleDownloadPDF = async (poId, e) => {
    e?.stopPropagation();
    try {
      const { blob, filename } = await purchaseOrdersAPI.downloadPDF(poId);
      downloadBlob(blob, filename);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };
  
  // Handle bulk PDF export
  const handleBulkExport = async (format = 'merged') => {
    if (selectedIds.length === 0) return;
    
    setExportLoading(true);
    try {
      const { blob, filename } = await purchaseOrdersAPI.exportPDFs(selectedIds, format);
      downloadBlob(blob, filename);
      toast.success(`Exported ${selectedIds.length} PO(s)`);
    } catch (err) {
      toast.error('Failed to export PDFs');
    } finally {
      setExportLoading(false);
    }
  };
  
  // Handle export filtered (all visible POs matching current filters)
  const handleExportFiltered = async (format = 'merged') => {
    if (pos.length === 0) {
      toast.error('No POs to export');
      return;
    }
    
    setExportLoading(true);
    try {
      const ids = pos.map(po => po.id);
      const { blob, filename } = await purchaseOrdersAPI.exportPDFs(ids, format);
      downloadBlob(blob, filename);
      toast.success(`Exported ${ids.length} PO(s)`);
    } catch (err) {
      toast.error('Failed to export PDFs');
    } finally {
      setExportLoading(false);
    }
  };
  
  // Fetch vendors for filter dropdown
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const data = await vendorsAPI.getVendors({ is_active: true, limit: 200 });
        setVendors((data.vendors || []).map(v => ({ value: v.id, label: v.name })));
      } catch (err) {
        console.error('Failed to fetch vendors:', err);
      }
    };
    fetchVendors();
  }, []);
  
  // Fetch function for POs
  const fetchPOs = useCallback(async (params) => {
    return await purchaseOrdersAPI.getPurchaseOrders(params);
  }, []);
  
  // Use admin table hook
  const {
    data: pos,
    total,
    loading,
    error,
    page,
    pageSize,
    setPage,
    setPageSize,
    sortBy,
    sortDir,
    handleSort,
    search,
    setSearch,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
    selectedIds,
    setSelectedIds,
    clearSelection,
    selectedCount,
    density,
    setDensity,
    refresh,
  } = useAdminTable({
    fetchFn: fetchPOs,
    defaultSortBy: 'po_date',
    defaultSortDir: 'desc',
    defaultPageSize: 25,
    filterKeys: ['status', 'vendor_id', 'date_from', 'date_to', 'total_min', 'total_max'],
    transformResponse: (data) => ({
      items: data.purchase_orders || data.items || [],
      total: data.total || 0
    })
  });
  
  // Get blocked items for delete
  const getBlockedDeleteItems = () => {
    return pos.filter(po => selectedIds.includes(po.id)).filter(po => {
      return po.status === 'closed' || po.bills_count > 0;
    }).map(po => ({
      id: po.id,
      reason: po.status === 'closed' 
        ? `PO ${po.po_number} is closed`
        : `PO ${po.po_number} has associated bills`
    }));
  };
  
  // Handle bulk delete
  const handleBulkDelete = async (reason) => {
    setBulkActionLoading(true);
    try {
      const result = await purchaseOrdersAPI.bulkDelete({
        ids: selectedIds,
        reason
      });
      
      if (result.deleted_count > 0) {
        toast.success(`Deleted ${result.deleted_count} PO(s)`);
      }
      if (result.blocked_items?.length > 0) {
        toast.warning(`${result.blocked_items.length} PO(s) could not be deleted`);
      }
      
      setShowDeleteModal(false);
      clearSelection();
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to delete POs');
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  // Table columns
  const columns = [
    {
      key: 'po_number',
      header: 'PO #',
      sortable: true,
      sortKey: 'po_number',
      render: (value, row) => (
        <MonoCell 
          value={value} 
          onClick={() => onViewPO?.(row.id)}
          className="font-semibold"
        />
      )
    },
    {
      key: 'vendor_name',
      header: 'Vendor',
      sortable: false,
      render: (value) => (
        <span className="font-medium text-ivory-800 truncate max-w-[180px] block" title={value}>
          {value || '—'}
        </span>
      )
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (value) => <StatusCell value={value} statusConfig={STATUS_CONFIG} />
    },
    {
      key: 'total',
      header: 'Total',
      sortable: true,
      sortKey: 'total_cents',
      align: 'right',
      render: (value) => <CurrencyCell value={value} />
    },
    {
      key: 'line_count',
      header: 'Lines',
      sortable: false,
      align: 'center',
      render: (value) => (
        <span className="text-sm text-ivory-600">{value || 0}</span>
      )
    },
    {
      key: 'po_date',
      header: 'PO Date',
      sortable: true,
      render: (value) => <DateCell value={value} />
    },
    {
      key: 'actions',
      header: '',
      width: '120px',
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onViewPO?.(row.id);
            }}
            title="View PO"
            data-testid={`view-po-${row.po_number}`}
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleDownloadPDF(row.id, e)}
            title="Download PDF"
            data-testid={`download-po-${row.po_number}`}
          >
            <Download className="w-4 h-4 text-blue-600" />
          </Button>
        </div>
      )
    }
  ];
  
  const filterLabels = {
    status: 'Status',
    vendor_id: 'Vendor',
    date_from: 'From',
    date_to: 'To',
    total_min: 'Min Amount',
    total_max: 'Max Amount',
  };
  
  return (
    <div className="space-y-6" data-testid="admin-purchase-orders-enhanced">
      {/* Header with Export Filtered button */}
      <div className="flex items-center justify-between">
        <AdminListHeader
          title="Purchase Orders"
          subtitle={total > 0 ? `${total.toLocaleString()} total POs` : undefined}
          createLabel="Create PO"
          onCreate={onCreatePO}
          canCreate={isAdmin}
          onRefresh={refresh}
          refreshing={loading}
          density={density}
          onDensityChange={setDensity}
        />
        
        {/* Export Filtered Dropdown */}
        <div className="relative group">
          <Button
            variant="outline"
            size="sm"
            disabled={exportLoading || pos.length === 0}
            className="flex items-center gap-2"
            data-testid="export-filtered-btn"
          >
            {exportLoading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Export Filtered
          </Button>
          <div className="absolute hidden group-hover:block top-full right-0 mt-1 bg-white border rounded-lg shadow-lg py-1 z-50 min-w-[160px]">
            <button
              className="w-full px-4 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2"
              onClick={() => handleExportFiltered('merged')}
            >
              <FileText className="w-4 h-4" />
              Merged PDF
            </button>
            <button
              className="w-full px-4 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2"
              onClick={() => handleExportFiltered('zip')}
            >
              <FileArchive className="w-4 h-4" />
              ZIP (Individual)
            </button>
          </div>
        </div>
      </div>
      
      <FilterBar hasActiveFilters={hasActiveFilters} onClearAll={clearFilters}>
        <GlobalSearch
          value={search}
          onChange={setSearch}
          placeholder="Search PO #, vendor..."
          className="flex-1 min-w-[200px]"
        />
        
        <StatusFilter
          value={filters.status}
          onChange={(v) => setFilter('status', v)}
          options={PO_STATUSES}
          placeholder="All Statuses"
          className="w-[150px]"
        />
        
        <StatusFilter
          value={filters.vendor_id}
          onChange={(v) => setFilter('vendor_id', v)}
          options={vendors}
          placeholder="All Vendors"
          className="w-[180px]"
        />
        
        <DateRangeFilter
          fromValue={filters.date_from}
          toValue={filters.date_to}
          onFromChange={(v) => setFilter('date_from', v)}
          onToChange={(v) => setFilter('date_to', v)}
          label="PO Date"
          className="w-[180px]"
        />
        
        <NumericRangeFilter
          minValue={filters.total_min}
          maxValue={filters.total_max}
          onMinChange={(v) => setFilter('total_min', v)}
          onMaxChange={(v) => setFilter('total_max', v)}
          label="Amount"
          prefix="$"
          className="w-[180px]"
        />
      </FilterBar>
      
      <ActiveFilterChips
        filters={filters}
        filterLabels={filterLabels}
        onRemove={(key) => setFilter(key, null)}
        onClearAll={clearFilters}
      />
      
      <BulkActionToolbar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
      >
        {/* Export Selected - Dropdown with format options */}
        <div className="relative group">
          <Button
            variant="outline"
            size="sm"
            disabled={exportLoading}
            className="flex items-center gap-1"
            onClick={() => handleBulkExport('merged')}
            data-testid="export-selected-btn"
          >
            {exportLoading ? (
              <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-1" />
            )}
            Export Selected
          </Button>
          <div className="absolute hidden group-hover:block top-full left-0 mt-1 bg-white border rounded-lg shadow-lg py-1 z-50 min-w-[160px]">
            <button
              className="w-full px-4 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2"
              onClick={() => handleBulkExport('merged')}
            >
              <FileText className="w-4 h-4" />
              Merged PDF
            </button>
            <button
              className="w-full px-4 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2"
              onClick={() => handleBulkExport('zip')}
            >
              <FileArchive className="w-4 h-4" />
              ZIP (Individual)
            </button>
          </div>
        </div>
        
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDeleteModal(true)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        )}
      </BulkActionToolbar>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {error}
        </div>
      )}
      
      <AdminDataTable
        tableId="purchase-orders"
        data={pos}
        columns={columns}
        loading={loading}
        emptyMessage="No purchase orders found"
        emptyIcon={ShoppingBag}
        selectable={true}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        getRowId={(row) => row.id}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        density={density}
        onDensityChange={setDensity}
        showColumnSelector={true}
        resizableColumns={true}
        onRowClick={(row) => onViewPO?.(row.id)}
        data-testid="purchase-orders-data-table"
      />
      
      <DeleteConfirmationModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        onConfirm={handleBulkDelete}
        count={selectedCount}
        itemType="purchase order"
        loading={bulkActionLoading}
        blockedItems={getBlockedDeleteItems()}
        warnings={[
          'Closed POs cannot be deleted.',
          'POs with associated bills cannot be deleted.'
        ]}
        requireReason={true}
      />
    </div>
  );
};

export default AdminPurchaseOrdersEnhanced;
